import React, { useState, useEffect, useRef, useCallback } from "react"
import Section from "../../../../../../../components/Lp/Molecules/Section"
import { SectionRefs, RegisterCommonProps } from "./types"
import { useGtm } from "../../../../../../../hooks/useGtm"

export default function InstagramRegisterCommon({
    sectionsData,
    fixedBannerData,
    bannerLink,
    styles,
    overrideOverflowXHidden,
}: RegisterCommonProps) {
    useGtm(["GTM-MJ9GMNND", "GTM-PZVGNPT"])

    const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
    const sectionRefs = useRef<SectionRefs>([])
    const bannerRef = useRef<HTMLDivElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [bannerHeight, setBannerHeight] = useState(0)
    const [isTouchDevice, setIsTouchDevice] = useState(false)
    const wheelTimeoutRef = useRef<number | null>(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const NOT_FOUND_INDEX = -1
                        const index = sectionRefs.current.findIndex(
                            ref => ref === (entry.target as HTMLDivElement)
                        )
                        if (index !== NOT_FOUND_INDEX) {
                            setCurrentSectionIndex(index)
                        }
                    }
                })
            },
            {
                root: null,
                rootMargin: "-50% 0px -50% 0px",
                threshold: 0,
            }
        )

        sectionRefs.current.forEach(ref => {
            if (ref) observer.observe(ref)
        })

        return () => {
            sectionRefs.current.forEach(ref => {
                if (ref) observer.unobserve(ref)
            })
        }
    }, [sectionRefs.current.length])

    const scrollToSection = useCallback((index: number) => {
        if (sectionRefs.current[index]) {
            sectionRefs.current[index]?.scrollIntoView({ behavior: "smooth" })
        }
    }, [])

    useEffect(() => {
        const checkTouchDevice = () => {
            setIsTouchDevice(
                "ontouchstart" in window || navigator.maxTouchPoints > 0
            )
        }
        checkTouchDevice()
    }, [])

    useEffect(() => {
        if (overrideOverflowXHidden) {
            const originalStyle = document.body.style.overflowX
            document.body.style.overflowX = "hidden"
            return () => {
                document.body.style.overflowX = originalStyle
            }
        }
    }, [overrideOverflowXHidden])

    useEffect(() => {
        if (isTouchDevice || !containerRef.current) return

        const container = containerRef.current
        let isScrolling = false
        const SCROLL_THRESHOLD = 50
        const SCROLL_ANIMATION_DURATION = 800
        const handleWheel = (e: WheelEvent) => {
            if (isScrolling) {
                e.preventDefault()
                return
            }

            const deltaY = e.deltaY

            if (
                deltaY > SCROLL_THRESHOLD &&
                currentSectionIndex < sectionsData.length - 1
            ) {
                e.preventDefault()
                isScrolling = true
                scrollToSection(currentSectionIndex + 1)
                setTimeout(() => {
                    isScrolling = false
                }, SCROLL_ANIMATION_DURATION)
            } else if (deltaY < -SCROLL_THRESHOLD && currentSectionIndex > 0) {
                e.preventDefault()
                isScrolling = true
                scrollToSection(currentSectionIndex - 1)
                setTimeout(() => {
                    isScrolling = false
                }, SCROLL_ANIMATION_DURATION)
            }
        }

        container.addEventListener("wheel", handleWheel, { passive: false })

        return () => {
            container.removeEventListener("wheel", handleWheel)
            if (wheelTimeoutRef.current) {
                clearTimeout(wheelTimeoutRef.current)
            }
        }
    }, [
        currentSectionIndex,
        isTouchDevice,
        sectionsData.length,
        scrollToSection,
    ])

    useEffect(() => {
        const updateBannerHeight = () => {
            if (bannerRef.current) {
                const height = bannerRef.current.offsetHeight
                setBannerHeight(height)
            }
        }

        updateBannerHeight()

        const bannerImage = bannerRef.current?.querySelector("img")
        if (bannerImage) {
            if (bannerImage.complete) {
                updateBannerHeight()
            } else {
                bannerImage.addEventListener("load", updateBannerHeight)
            }
        }

        window.addEventListener("resize", updateBannerHeight)

        return () => {
            window.removeEventListener("resize", updateBannerHeight)
            if (bannerImage) {
                bannerImage.removeEventListener("load", updateBannerHeight)
            }
        }
    }, [fixedBannerData.img])

    return (
        <div className={styles.lpContainer}>
            <div
                ref={containerRef}
                className={styles.verticalSectionsContainer}
                style={{
                    scrollPaddingBottom: `${bannerHeight}px`,
                    scrollSnapType: "y mandatory",
                }}
            >
                {sectionsData.map((section, index) => (
                    <Section
                        key={section.id}
                        id={section.id}
                        title={section.title}
                        img={section.img}
                        ref={(el: HTMLDivElement | null) =>
                            (sectionRefs.current[index] = el)
                        }
                        label={section.label}
                    />
                ))}
            </div>

            <div className={styles.paginationVertical}>
                {sectionsData.map((_, index) => (
                    <span
                        key={index}
                        className={`${styles.dotVertical} ${index === currentSectionIndex ? styles.active : ""}`}
                        onClick={() => scrollToSection(index)}
                    ></span>
                ))}
            </div>

            <div className={styles.fixedBanner} ref={bannerRef}>
                <a href={bannerLink}>
                    <img
                        src={fixedBannerData.img}
                        alt={fixedBannerData.alt}
                        className={styles.bannerImage}
                        data-click-label={fixedBannerData.label}
                    />
                </a>
            </div>
        </div>
    )
}
